/**
 * Onboarding Service
 *
 * API service for onboarding operations including
 * AI-powered profile extraction from documents.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';
import type { IExtractedProfileData, ExtractProfileSource } from '@tonnie/types';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface ExtractProfileResult {
  success: boolean;
  data: IExtractedProfileData;
  meta: {
    source: ExtractProfileSource;
    extractedAt: string;
    aiPowered: boolean;
  };
}

export interface OnboardingProgress {
  currentStep: number;
  completedSteps: string[];
  data: Record<string, unknown>;
  savedAt: string | null;
  completedAt: string | null;
  // B2B extras returned by BusinessService.getTenantOnboardingProgress.
  // Present only when the user belongs to a tenant that has completed
  // (or is completing) the business onboarding wizard.
  profileCompletionPct?: number;
  defaultProgramId?: string | null;
  defaultCohortId?: string | null;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class OnboardingService {
  constructor(private readonly client: TonnieClient) {}

  /**
   * Get the current onboarding progress (draft data).
   * Returns saved step, completed steps, and all form data.
   */
  async getOnboardingProgress(): Promise<OnboardingProgress> {
    return this.client.get<OnboardingProgress>('/api/users/me/onboarding/progress');
  }

  /**
   * Extract structured profile data from an uploaded document.
   * Supports PDF, DOCX, DOC, and TXT files.
   */
  async extractProfile(
    file: File,
    source: ExtractProfileSource = 'other'
  ): Promise<ExtractProfileResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);

    // No explicit budget. This call used to pass `{ timeout: 60_000 }` — the
    // one upload in the repo that escaped the 30 s cut (H2) — but 60 s was
    // still a wall clock: a CV on a slow link was aborted mid-transfer while
    // making progress. `postForm` now bounds every upload by a stall window.
    return this.client.postForm<ExtractProfileResult>(
      API_ENDPOINTS.ONBOARDING.EXTRACT_PROFILE,
      formData
    );
  }
}
